import { useTheme } from "@/src/styles/theme-provider";
import { Eye, EyeSlash } from "iconsax-react-nativejs";
import { cssInterop } from "nativewind";
import { forwardRef, useState } from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  NativeSyntheticEvent,
  Pressable,
  ReturnKeyTypeOptions,
  Text,
  TextInput,
  TextInputProps,
  TextInputSubmitEditingEventData,
  View,
} from "react-native";

cssInterop(Eye, {
  className: {
    target: "style",
    nativeStyleToProp: {
      color: true,
      opacity: true,
    },
  },
});

cssInterop(EyeSlash, {
  className: {
    target: "style",
    nativeStyleToProp: {
      color: true,
      opacity: true,
    },
  },
});

type PasswordProps<T extends FieldValues> = Readonly<{
  label?: string;
  placeholder?: string;
  keyboardType?: "default" | "email-address" | "numeric" | "phone-pad";
  control: Control<T>;
  name: Path<T>;
  error?: string;
  rules?: RegisterOptions<T, Path<T>>;
  returnKeyType?: ReturnKeyTypeOptions;
  blurOnSubmit?: boolean;
  onSubmitEditing?: (
    e: NativeSyntheticEvent<TextInputSubmitEditingEventData>,
  ) => void;
  /**
   * Names the field's role for autofill: "current-password" when signing in,
   * "new-password" when setting or resetting one. Left unset,
   * react-native-web emits a bare `autocomplete="on"`, which identifies
   * nothing, so the browser never recognises a credential to save.
   */
  autoComplete?: TextInputProps["autoComplete"];
}>;

function PasswordFieldInner<T extends FieldValues>(
  {
    placeholder,
    label,
    control,
    name,
    error,
    rules,
    returnKeyType,
    blurOnSubmit,
    onSubmitEditing,
    autoComplete,
  }: PasswordProps<T>,
  ref: React.Ref<TextInput>,
) {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const { t } = useTranslation();
  const { themeVars } = useTheme();

  const displayLabel = label ?? t("LABEL_PASSWORD");
  const displayPlaceholder = placeholder ?? t("LABEL_PASSWORD");

  return (
    <View className="flex-1 mb-6 web:mb-4">
      <Text className="pb-2 font-medium text-onyx">{displayLabel}</Text>

      <View>
        <Controller
          control={control}
          name={name}
          rules={rules}
          render={({ field: { onChange, onBlur, value } }) => (
            <TextInput
              ref={ref}
              value={value}
              onChangeText={onChange}
              placeholder={displayPlaceholder}
              secureTextEntry={!passwordVisible}
              autoComplete={autoComplete}
              returnKeyType={returnKeyType}
              blurOnSubmit={blurOnSubmit}
              onSubmitEditing={onSubmitEditing}
              onFocus={() => setIsFocused(true)}
              onBlur={() => {
                setIsFocused(false);
                onBlur();
              }}
              placeholderTextColor={
                themeVars["--color-slate-default"] as string
              }
              className={`relative w-full rounded-lg bg-obsidian h-16 web:h-10 px-4 pr-12 text-onyx focus:outline-none border
                ${error ? "border-crimson" : isFocused ? "border-primary" : "border-ash-3"}`}
            />
          )}
        />
        <Pressable
          onPress={() => setPasswordVisible((v) => !v)}
          className="absolute right-4 top-1/2 -translate-y-1/2 hover:rounded-full hover:bg-ash-3"
          hitSlop={10}
        >
          {passwordVisible ? (
            <Eye size={20} className="text-slate" variant="Outline" />
          ) : (
            <EyeSlash size={20} className="text-slate" variant="Outline" />
          )}
        </Pressable>
      </View>

      {error && <Text className="text-crimson text-xs mt-1">{t(error)}</Text>}
    </View>
  );
}

const PasswordField = forwardRef(PasswordFieldInner) as <T extends FieldValues>(
  props: PasswordProps<T> & { ref?: React.Ref<TextInput> },
) => React.ReactElement;

export default PasswordField;
